/** Namespace tag so the host page can distinguish player messages from any other postMessage traffic. */
export const BRIDGE_SOURCE = "visorxr" as const;

export type BridgeMessage =
  | { source: typeof BRIDGE_SOURCE; type: "ar-ready" }
  | { source: typeof BRIDGE_SOURCE; type: "photo-captured"; dataURL: string }
  | { source: typeof BRIDGE_SOURCE; type: "error"; code: string; message: string };

/** The injected sink — in the player this wraps `window.parent.postMessage`. */
export interface MessagePoster {
  (message: BridgeMessage, targetOrigin: string): void;
}

/**
 * Player→parent side of the bridge. The target origin is REQUIRED — never a wildcard — because the
 * photo-captured message carries the user's capture; broadcasting it would leak it to whatever
 * origin happens to occupy the parent frame. Resolve the origin with {@link resolveBridgeTarget}.
 */
export class ParentBridge {
  constructor(
    private readonly poster: MessagePoster,
    private readonly targetOrigin: string,
  ) {}

  ready(): void {
    this.poster({ source: BRIDGE_SOURCE, type: "ar-ready" }, this.targetOrigin);
  }

  photo(dataURL: string): void {
    this.poster({ source: BRIDGE_SOURCE, type: "photo-captured", dataURL }, this.targetOrigin);
  }

  error(code: string, message: string): void {
    this.poster({ source: BRIDGE_SOURCE, type: "error", code, message }, this.targetOrigin);
  }
}

/**
 * Parse a comma-separated allowlist (from TRUSTED build config) into normalized origins. Each entry
 * is reduced to its URL origin, so "https://host/" and "https://host" both match a referrer origin.
 */
export function parseAllowedParentOrigins(raw: string | undefined): string[] {
  if (!raw || !raw.trim()) return [];
  return raw
    .split(",")
    .map((entry) => entry.trim())
    .map((entry) => {
      try {
        return new URL(entry).origin;
      } catch {
        return null;
      }
    })
    .filter((origin): origin is string => origin !== null);
}

/**
 * Resolve the exact parent origin to address postMessage to. Fails closed (returns null → don't post
 * sensitive data) unless the referrer's origin is explicitly approved by `allowedOrigins`. An empty
 * allowlist authorizes nobody: secure by default, so a build that forgets to configure trusted
 * origins never leaks captures to an arbitrary embedder. The allowlist must come from trusted config,
 * never the (attacker-influenceable) experience descriptor.
 */
export function resolveBridgeTarget(referrer: string, allowedOrigins: readonly string[]): string | null {
  if (!referrer || allowedOrigins.length === 0) return null;

  let origin: string;
  try {
    origin = new URL(referrer).origin;
  } catch {
    return null;
  }

  return allowedOrigins.includes(origin) ? origin : null;
}

/**
 * Parent-side parser. Returns a typed message only when the payload required by that message type is
 * actually present and well-typed, so malformed traffic can't masquerade as a valid BridgeMessage.
 */
export function parseBridgeMessage(data: unknown): BridgeMessage | null {
  if (typeof data !== "object" || data === null) return null;
  const message = data as Record<string, unknown>;
  if (message.source !== BRIDGE_SOURCE) return null;

  switch (message.type) {
    case "ar-ready":
      return { source: BRIDGE_SOURCE, type: "ar-ready" };
    case "photo-captured":
      return typeof message.dataURL === "string"
        ? { source: BRIDGE_SOURCE, type: "photo-captured", dataURL: message.dataURL }
        : null;
    case "error":
      return typeof message.code === "string" && typeof message.message === "string"
        ? { source: BRIDGE_SOURCE, type: "error", code: message.code, message: message.message }
        : null;
    default:
      return null;
  }
}
